// Mock skin analysis data for development

import type {
  AnalysisResults,
  SkinMetrics,
  Heatmaps,
  EnhancedAnalysisResults,
  EnhancedMetrics,
  MetricDetail,
  MetricKey,
} from '@/types/analysis'
import { getMetricStatus } from '@/types/analysis'
import { getBenchmark } from '@/data/ageBenchmarks'
import { METRIC_INFO } from '@/data/metricExplanations'

export const mockMetrics: SkinMetrics = {
  pores: 45,
  texture: 35,
  pigmentation: 42,
  redness: 28,
  wrinkles: 22,
  brightness: 68,
  oiliness: 52,
  acne: 18,
}

export const mockHeatmaps: Heatmaps = {
  pores: '/image/mock/heatmap-pores.png',
  texture: '/image/mock/heatmap-texture.png',
  pigmentation: '/image/mock/heatmap-pigmentation.png',
  wrinkles: '/image/mock/heatmap-wrinkles.png',
  redness: '/image/mock/heatmap-redness.png',
}

export const mockAnalysisResults: AnalysisResults = {
  skinAge: 28,
  skinScore: 72,
  skinType: 'combination',
  metrics: mockMetrics,
  heatmaps: mockHeatmaps,
  photoUrl: '/image/hero-model.jpeg',
  analyzedAt: new Date().toISOString(),
}

// Alternative mock data for testing different scenarios
export const mockAnalysisOily: AnalysisResults = {
  skinAge: 25,
  skinScore: 65,
  skinType: 'oily',
  metrics: {
    pores: 62,
    texture: 48,
    pigmentation: 35,
    redness: 22,
    wrinkles: 15,
    brightness: 72,
    oiliness: 78,
    acne: 45,
  },
  heatmaps: mockHeatmaps,
  photoUrl: '/image/hero-model.jpeg',
  analyzedAt: new Date().toISOString(),
}

export const mockAnalysisDry: AnalysisResults = {
  skinAge: 34,
  skinScore: 68,
  skinType: 'dry',
  metrics: {
    pores: 28,
    texture: 52,
    pigmentation: 48,
    redness: 42,
    wrinkles: 38,
    brightness: 55,
    oiliness: 18,
    acne: 12,
  },
  heatmaps: mockHeatmaps,
  photoUrl: '/image/hero-model.jpeg',
  analyzedAt: new Date().toISOString(),
}

// ===== ENHANCED MOCK DATA FOR PREMIUM $99 REPORT =====

// Mock user age for benchmark comparison
const MOCK_USER_AGE = 30

// Raw scores for all 14 metrics (0-100, higher = worse condition)
const mockEnhancedScores: Record<MetricKey, number> = {
  pores: 58,
  wrinkles: 25,
  texture: 42,
  acne: 22,
  redness: 35,
  darkCircles: 48,
  eyeBags: 32,
  spots: 38,
  oiliness: 52,
  moisture: 35,
  firmness: 28,
  radiance: 32,
  droopyEyelidUpper: 18,
  droopyEyelidLower: 15,
}

// Zone-specific scores for metrics that support zones
const mockZoneScores: Partial<Record<MetricKey, Record<string, number>>> = {
  pores: {
    forehead: 62,
    nose: 72,
    cheeks: 45,
  },
  wrinkles: {
    forehead: 28,
    crowsFeet: 22,
    glabella: 18,
    nasolabial: 32,
    marionette: 15,
    periocular: 25,
  },
  oiliness: {
    tZone: 68,
    uZone: 35,
  },
}

// Mock heatmap URLs (these would come from YouCam API in production)
const mockEnhancedHeatmaps: Record<MetricKey, string> = {
  pores: '/image/mock/heatmap-pores.png',
  wrinkles: '/image/mock/heatmap-wrinkles.png',
  texture: '/image/mock/heatmap-texture.png',
  acne: '/image/mock/heatmap-acne.png',
  redness: '/image/mock/heatmap-redness.png',
  darkCircles: '/image/mock/heatmap-darkcircles.png',
  eyeBags: '/image/mock/heatmap-eyebags.png',
  spots: '/image/mock/heatmap-spots.png',
  oiliness: '/image/mock/heatmap-oiliness.png',
  moisture: '/image/mock/heatmap-moisture.png',
  firmness: '/image/mock/heatmap-firmness.png',
  radiance: '/image/mock/heatmap-radiance.png',
  droopyEyelidUpper: '/image/mock/heatmap-eyelid-upper.png',
  droopyEyelidLower: '/image/mock/heatmap-eyelid-lower.png',
}

// Helper to create a MetricDetail object
function createMetricDetail(key: MetricKey, userAge: number): MetricDetail {
  const score = mockEnhancedScores[key]
  const ageAverage = getBenchmark(userAge, key)
  const info = METRIC_INFO[key]

  return {
    key,
    name: info.name,
    category: info.category,
    score,
    ageAverage,
    status: getMetricStatus(score, ageAverage),
    zones: mockZoneScores[key],
    heatmapUrl: mockEnhancedHeatmaps[key],
    zoneHeatmaps: info.hasZones
      ? Object.keys(mockZoneScores[key] || {}).reduce(
          (acc, zone) => ({
            ...acc,
            [zone]: `/image/mock/heatmap-${key}-${zone}.png`,
          }),
          {}
        )
      : undefined,
  }
}

// Create all 14 metric details
function createEnhancedMetrics(userAge: number): EnhancedMetrics {
  return {
    pores: createMetricDetail('pores', userAge),
    wrinkles: createMetricDetail('wrinkles', userAge),
    texture: createMetricDetail('texture', userAge),
    acne: createMetricDetail('acne', userAge),
    redness: createMetricDetail('redness', userAge),
    darkCircles: createMetricDetail('darkCircles', userAge),
    eyeBags: createMetricDetail('eyeBags', userAge),
    spots: createMetricDetail('spots', userAge),
    oiliness: createMetricDetail('oiliness', userAge),
    moisture: createMetricDetail('moisture', userAge),
    firmness: createMetricDetail('firmness', userAge),
    radiance: createMetricDetail('radiance', userAge),
    droopyEyelidUpper: createMetricDetail('droopyEyelidUpper', userAge),
    droopyEyelidLower: createMetricDetail('droopyEyelidLower', userAge),
  }
}

// Get top 3 concerns (metrics with lowest scores = worst areas needing attention)
function getTopConcernKeys(metrics: EnhancedMetrics): MetricKey[] {
  const entries = Object.entries(metrics) as [MetricKey, MetricDetail][]
  return entries
    .sort((a, b) => a[1].score - b[1].score)
    .slice(0, 3)
    .map(([key]) => key)
}

// Main enhanced mock analysis
export const mockEnhancedAnalysis: EnhancedAnalysisResults = (() => {
  const metrics = createEnhancedMetrics(MOCK_USER_AGE)
  return {
    skinAge: 28,
    skinScore: 72,
    skinType: 'combination',
    photoUrl: '/image/hero-model.jpeg',
    metrics,
    topConcerns: getTopConcernKeys(metrics),
    analyzedAt: new Date().toISOString(),
  }
})()

// Helper to create enhanced analysis from survey data
export function createEnhancedAnalysisFromAge(userAge: number): EnhancedAnalysisResults {
  const metrics = createEnhancedMetrics(userAge)
  return {
    skinAge: Math.max(18, Math.min(70, userAge - 2)), // Skin age slightly younger
    skinScore: 72,
    skinType: 'combination',
    photoUrl: '/image/hero-model.jpeg',
    metrics,
    topConcerns: getTopConcernKeys(metrics),
    analyzedAt: new Date().toISOString(),
  }
}
